<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchGhlPaymentCatalogProduct,
  submitGhlCheckoutLink
} from '@/api/ghlPayment.api';
import {
  fetchShippingQuote,
  selectedShippingCost,
  shippingSelectionReady
} from '@/utils/checkoutShippingQuote';
import { redirectToPayDollar as performPayDollarRedirect } from '@/utils/paydollarRedirect';
import { getCurrentUser } from '@/utils/index';
import {
  PH_COUNTRY_ID,
  emptyAddress,
  fetchCountries,
  fetchZones,
  findCountryId,
  findZoneId,
  toPositiveInt,
  zoneName
} from '@/utils/checkoutAddress';

const route = useRoute();
const router = useRouter();

const MIN_QTY = 1;
const MAX_QTY = 99;

const loading = ref(true);
const submitting = ref(false);
const proceeding = ref(false);
const errorMessage = ref('');
const product = ref(null);
const paymentStatus = ref('');
const checkoutPhase = ref('details');
const pendingGateway = ref(null);
const orderSummary = ref(null);
const shippingMethods = ref([]);
const selectedShippingMethod = ref('');
const requiresShipping = ref(false);
const quotingShipping = ref(false);
const shippingQuoteError = ref('');

const shippingQuoteState = {
  shippingMethods,
  selectedShippingMethod,
  requiresShipping,
  quotingShipping,
  shippingQuoteError
};

const contactForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: ''
});

const addressForm = ref(emptyAddress());
const shippingForm = ref(emptyAddress());
const sameShippingAsBilling = ref(true);
const countries = ref([]);
const billingZones = ref([]);
const shippingZones = ref([]);
const pendingZoneHint = {
  billing: '',
  shipping: ''
};

const insuranceForm = ref({
  fname: '',
  mname: '',
  lname: '',
  dob: '',
  nationality: 'Philippines',
  jobClassification: '1',
  jobClassificationText: '',
  contact: '',
  email: '',
  street: '',
  country: 'Philippines',
  state: '',
  postalCode: '',
  municipality: '',
  address_country: 0,
  address_province: 0,
  address_country2: 0,
  benefFname: '',
  benefMname: '',
  benefLname: '',
  benefDob: '',
  benefContact: '',
  benefRelationship: ''
});

const locationId = computed(() => String(route.query.locationId || '').trim());
const sku = computed(() => String(route.query.sku || '').trim());
const ecpid = computed(() => String(route.query.ecpid || '').trim());
const prefillToken = computed(() => String(route.query.pf || '').trim());

const PREFILL_CACHE_PREFIX = 'ghl:pay:prefill:';
const PREFILL_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function prefillCacheKey(token) {
  const normalized = String(token || '').trim();
  if (!normalized) return '';
  return `${PREFILL_CACHE_PREFIX}${normalized}`;
}

function loadCachedPrefill(token) {
  const key = prefillCacheKey(token);
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const savedAt = Number(parsed.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > PREFILL_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      firstName: String(parsed.firstName || '').trim(),
      lastName: String(parsed.lastName || '').trim(),
      email: String(parsed.email || '').trim(),
      phone: String(parsed.phone || '').trim(),
      address1: String(parsed.address1 || '').trim(),
      city: String(parsed.city || '').trim(),
      postcode: String(parsed.postcode || '').trim(),
      countryId: toPositiveInt(parsed.countryId),
      zoneId: toPositiveInt(parsed.zoneId),
      state: String(parsed.state || '').trim(),
      shippingAddress1: String(parsed.shippingAddress1 || '').trim(),
      shippingCity: String(parsed.shippingCity || '').trim(),
      shippingPostcode: String(parsed.shippingPostcode || '').trim(),
      shippingCountryId: toPositiveInt(parsed.shippingCountryId),
      shippingZoneId: toPositiveInt(parsed.shippingZoneId),
      shippingState: String(parsed.shippingState || '').trim(),
      sameShippingAsBilling: parsed.sameShippingAsBilling !== false
    };
  } catch {
    return null;
  }
}

function saveCachedPrefill(token, prefill) {
  const key = prefillCacheKey(token);
  if (!key || !prefill) return;

  try {
    localStorage.setItem(key, JSON.stringify({
      savedAt: Date.now(),
      firstName: String(prefill.firstName || '').trim(),
      lastName: String(prefill.lastName || '').trim(),
      email: String(prefill.email || '').trim(),
      phone: String(prefill.phone || '').trim(),
      address1: String(prefill.address1 || '').trim(),
      city: String(prefill.city || '').trim(),
      postcode: String(prefill.postcode || '').trim(),
      countryId: toPositiveInt(prefill.countryId),
      zoneId: toPositiveInt(prefill.zoneId),
      state: String(prefill.state || '').trim(),
      shippingAddress1: String(prefill.shippingAddress1 || '').trim(),
      shippingCity: String(prefill.shippingCity || '').trim(),
      shippingPostcode: String(prefill.shippingPostcode || '').trim(),
      shippingCountryId: toPositiveInt(prefill.shippingCountryId),
      shippingZoneId: toPositiveInt(prefill.shippingZoneId),
      shippingState: String(prefill.shippingState || '').trim(),
      sameShippingAsBilling: prefill.sameShippingAsBilling !== false
    }));
  } catch {
    // Safari private mode and quota errors are non-fatal.
  }
}

function hasPrefillValues(prefill) {
  if (!prefill || typeof prefill !== 'object') return false;
  return Object.entries(prefill).some(([key, value]) => {
    if (key === 'sameShippingAsBilling') return false;
    return Boolean(value);
  });
}

function loadLoggedInUserPrefill() {
  const user = getCurrentUser();
  if (!user) return null;

  const prefill = {
    firstName: String(user.fname || '').trim(),
    lastName: String(user.lname || '').trim(),
    email: String(user.email || '').trim(),
    phone: String(user.mobile || '').trim(),
    address1: String(user.address || '').trim(),
    city: String(user.city || '').trim(),
    postcode: String(user.postcode || user.postalCode || '').trim(),
    countryId: toPositiveInt(user.country_id || user.countryId),
    zoneId: toPositiveInt(user.zone_id || user.zoneId),
    state: String(user.state || '').trim()
  };

  return hasPrefillValues(prefill) ? prefill : null;
}

function applyAddressPrefill(target, prefill, keys) {
  if (!prefill) return;
  keys.forEach(({ from, to }) => {
    const value = String(prefill[from] || '').trim();
    if (value) target[to] = value;
  });
}

function selectedStateName(form, zones) {
  return zoneName(zones, form.zoneId);
}

function syncAddressToInsurance() {
  if (addressForm.value.address1) insuranceForm.value.street = addressForm.value.address1;
  if (addressForm.value.city) insuranceForm.value.municipality = addressForm.value.city;
  const state = selectedStateName(addressForm.value, billingZones.value);
  if (state) insuranceForm.value.state = state;
  if (addressForm.value.postcode) insuranceForm.value.postalCode = addressForm.value.postcode;
  if (addressForm.value.countryId) insuranceForm.value.address_country = addressForm.value.countryId;
  if (addressForm.value.zoneId) insuranceForm.value.address_province = addressForm.value.zoneId;
}

function applyCountryPrefill(form, prefill, keys, hintKey) {
  const countryId = findCountryId(
    countries.value,
    prefill[keys.countryId] || prefill[keys.country]
  );
  if (countryId) form.countryId = countryId;

  const zoneId = toPositiveInt(prefill[keys.zoneId]);
  if (zoneId) {
    form.zoneId = zoneId;
    pendingZoneHint[hintKey] = '';
    return;
  }

  const stateHint = String(prefill[keys.state] || '').trim();
  if (stateHint) pendingZoneHint[hintKey] = stateHint;
}

function applyPrefillValues(prefill) {
  if (!prefill) return;

  if (prefill.firstName) contactForm.value.firstName = String(prefill.firstName).trim();
  if (prefill.lastName) contactForm.value.lastName = String(prefill.lastName).trim();
  if (prefill.email) contactForm.value.email = String(prefill.email).trim();
  if (prefill.phone) contactForm.value.phone = String(prefill.phone).trim();

  applyAddressPrefill(addressForm.value, prefill, [
    { from: 'address1', to: 'address1' },
    { from: 'city', to: 'city' },
    { from: 'postcode', to: 'postcode' }
  ]);
  applyCountryPrefill(addressForm.value, prefill, {
    countryId: 'countryId',
    country: 'country',
    zoneId: 'zoneId',
    state: 'state'
  }, 'billing');

  if (prefill.sameShippingAsBilling === false) {
    sameShippingAsBilling.value = false;
  }

  applyAddressPrefill(shippingForm.value, prefill, [
    { from: 'shippingAddress1', to: 'address1' },
    { from: 'shippingCity', to: 'city' },
    { from: 'shippingPostcode', to: 'postcode' }
  ]);
  applyCountryPrefill(shippingForm.value, prefill, {
    countryId: 'shippingCountryId',
    country: 'shippingCountry',
    zoneId: 'shippingZoneId',
    state: 'shippingState'
  }, 'shipping');

  insuranceForm.value.email = contactForm.value.email;
  insuranceForm.value.contact = contactForm.value.phone;
  if (contactForm.value.firstName) insuranceForm.value.fname = contactForm.value.firstName;
  if (contactForm.value.lastName) insuranceForm.value.lname = contactForm.value.lastName;
  syncAddressToInsurance();
}

function currentPrefillSnapshot() {
  return {
    firstName: contactForm.value.firstName,
    lastName: contactForm.value.lastName,
    email: contactForm.value.email,
    phone: contactForm.value.phone,
    address1: addressForm.value.address1,
    city: addressForm.value.city,
    postcode: addressForm.value.postcode,
    countryId: addressForm.value.countryId,
    zoneId: addressForm.value.zoneId,
    state: selectedStateName(addressForm.value, billingZones.value),
    shippingAddress1: shippingForm.value.address1,
    shippingCity: shippingForm.value.city,
    shippingPostcode: shippingForm.value.postcode,
    shippingCountryId: shippingForm.value.countryId,
    shippingZoneId: shippingForm.value.zoneId,
    shippingState: selectedStateName(shippingForm.value, shippingZones.value),
    sameShippingAsBilling: sameShippingAsBilling.value
  };
}

function buildAddressPayload(form, zones) {
  return {
    firstname: contactForm.value.firstName.trim(),
    lastname: contactForm.value.lastName.trim(),
    address1: form.address1.trim(),
    city: form.city.trim(),
    postcode: form.postcode.trim(),
    contact: contactForm.value.phone.trim(),
    state: selectedStateName(form, zones),
    country_id: toPositiveInt(form.countryId, PH_COUNTRY_ID),
    zone_id: toPositiveInt(form.zoneId)
  };
}

function addressIncomplete(form) {
  return !form.address1.trim()
    || !form.city.trim()
    || !form.postcode.trim()
    || !toPositiveInt(form.countryId)
    || !toPositiveInt(form.zoneId);
}

async function loadBillingZones(countryId, options = {}) {
  if (!options.keepZone) addressForm.value.zoneId = 0;
  billingZones.value = await fetchZones(countryId);
}

async function loadShippingZones(countryId, options = {}) {
  if (!options.keepZone) shippingForm.value.zoneId = 0;
  shippingZones.value = await fetchZones(countryId);
}

function applyPendingZone(form, zones, hintKey) {
  const resolved = findZoneId(zones, form.zoneId || pendingZoneHint[hintKey]);
  if (resolved) form.zoneId = resolved;
  pendingZoneHint[hintKey] = '';
}

async function hydrateAddressSelects() {
  if (!addressForm.value.countryId) addressForm.value.countryId = PH_COUNTRY_ID;
  await loadBillingZones(addressForm.value.countryId, { keepZone: true });
  applyPendingZone(addressForm.value, billingZones.value, 'billing');

  if (!sameShippingAsBilling.value) {
    if (!shippingForm.value.countryId) shippingForm.value.countryId = addressForm.value.countryId;
    await loadShippingZones(shippingForm.value.countryId, { keepZone: true });
    applyPendingZone(shippingForm.value, shippingZones.value, 'shipping');
  }
}

async function onBillingCountryChange() {
  await loadBillingZones(addressForm.value.countryId);
}

async function onShippingCountryChange() {
  await loadShippingZones(shippingForm.value.countryId);
}

function clampQuantity(value) {
  const parsed = parseInt(String(value == null || value === '' ? '1' : value), 10);
  if (Number.isNaN(parsed)) return MIN_QTY;
  return Math.min(MAX_QTY, Math.max(MIN_QTY, parsed));
}

const quantity = ref(clampQuantity(route.query.qty));
const isTerminalStatus = computed(() => ['success', 'fail', 'cancel'].includes(paymentStatus.value));
const isReviewPhase = computed(() => checkoutPhase.value === 'review');
const hasInsuranceCoverage = computed(() => Boolean(product.value && product.value.hasInsuranceCoverage));

function syncQuantityToUrl() {
  const nextQty = String(quantity.value);
  if (String(route.query.qty || '') === nextQty) return;
  router.replace({
    query: {
      ...route.query,
      qty: nextQty
    }
  });
}

function setQuantity(nextValue) {
  quantity.value = clampQuantity(nextValue);
  clearCheckoutReview();
  syncQuantityToUrl();
}

function decrementQuantity() {
  if (quantity.value <= MIN_QTY) return;
  setQuantity(quantity.value - 1);
}

function incrementQuantity() {
  if (quantity.value >= MAX_QTY) return;
  setQuantity(quantity.value + 1);
}

function onQuantityInput(event) {
  const raw = event.target.value;
  if (raw === '') return;
  quantity.value = clampQuantity(raw);
  clearCheckoutReview();
}

function onQuantityBlur() {
  quantity.value = clampQuantity(quantity.value);
  clearCheckoutReview();
  syncQuantityToUrl();
}

const lineTotal = computed(() => {
  if (!product.value) return 0;
  return Number(product.value.amount || 0) * quantity.value;
});

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP'
  }).format(Number(amount || 0));
}

const formattedLineAmount = computed(() => formatCurrency(lineTotal.value));
const formattedUnitAmount = computed(() => {
  if (!product.value) return '';
  return formatCurrency(product.value.amount);
});

const formattedUnitLineDetail = computed(() => {
  if (!product.value || quantity.value <= 1) return '';
  return `${formattedUnitAmount.value} × ${quantity.value}`;
});

const formattedReviewTotal = computed(() => {
  if (!orderSummary.value) return '';
  return formatCurrency(orderSummary.value.total);
});

const estimatedShippingCost = computed(() => selectedShippingCost(shippingQuoteState));

const estimatedTotal = computed(() => lineTotal.value + estimatedShippingCost.value);

const formattedEstimatedTotal = computed(() => formatCurrency(estimatedTotal.value));

const canProceedToReview = computed(() => (
  Boolean(product.value)
  && !quotingShipping.value
  && shippingSelectionReady(shippingQuoteState)
));

function resetShippingQuote() {
  shippingMethods.value = [];
  selectedShippingMethod.value = '';
  requiresShipping.value = false;
  shippingQuoteError.value = '';
}

function buildQuotePayload() {
  const billingAddress = buildAddressPayload(addressForm.value, billingZones.value);
  return {
    locationId: locationId.value,
    sku: sku.value,
    qty: quantity.value,
    contact: {
      name: fullName.value || 'Guest User',
      email: contactForm.value.email.trim() || 'guest@ai247.local',
      phone: contactForm.value.phone.trim(),
      billingAddress,
      shippingAddress: sameShippingAsBilling.value
        ? billingAddress
        : buildAddressPayload(shippingForm.value, shippingZones.value),
      sameShippingAsBilling: sameShippingAsBilling.value
    }
  };
}

function shippingDestinationReady() {
  const destination = sameShippingAsBilling.value ? addressForm.value : shippingForm.value;
  const zones = sameShippingAsBilling.value ? billingZones.value : shippingZones.value;
  return !addressIncomplete(destination) && zones.length > 0;
}

let quoteTimer = null;

function scheduleShippingQuote() {
  clearCheckoutReview();
  resetShippingQuote();

  if (!product.value || !shippingDestinationReady()) return;

  if (quoteTimer) clearTimeout(quoteTimer);
  quoteTimer = setTimeout(async () => {
    await fetchShippingQuote(buildQuotePayload(), shippingQuoteState);
  }, 400);
}

function clearCheckoutReview() {
  checkoutPhase.value = 'details';
  pendingGateway.value = null;
  orderSummary.value = null;
}

function backToDetails() {
  clearCheckoutReview();
  errorMessage.value = '';
}

const fullName = computed(() => {
  return [contactForm.value.firstName, contactForm.value.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
});

function applyQueryPrefills() {
  const fromQuery = {
    firstName: String(route.query.firstName || '').trim(),
    lastName: String(route.query.lastName || '').trim(),
    email: String(route.query.email || '').trim(),
    phone: String(route.query.phone || '').trim(),
    address1: String(route.query.address1 || route.query.line1 || '').trim(),
    city: String(route.query.city || '').trim(),
    postcode: String(route.query.postcode || route.query.zipCode || route.query.postalCode || '').trim(),
    countryId: toPositiveInt(route.query.countryId),
    country: String(route.query.country || '').trim(),
    zoneId: toPositiveInt(route.query.zoneId),
    state: String(route.query.state || '').trim()
  };
  const hasQueryPrefill = Object.values(fromQuery).some(Boolean);

  applyPrefillValues(loadLoggedInUserPrefill());
  applyPrefillValues(loadCachedPrefill(prefillToken.value));
  applyPrefillValues(fromQuery);

  if (prefillToken.value && hasQueryPrefill) {
    saveCachedPrefill(prefillToken.value, currentPrefillSnapshot());
  }
}

watch(
  () => [
    contactForm.value.firstName,
    contactForm.value.lastName,
    contactForm.value.email,
    contactForm.value.phone,
    addressForm.value.address1,
    addressForm.value.city,
    addressForm.value.postcode,
    addressForm.value.countryId,
    addressForm.value.zoneId,
    shippingForm.value.address1,
    shippingForm.value.city,
    shippingForm.value.postcode,
    shippingForm.value.countryId,
    shippingForm.value.zoneId,
    sameShippingAsBilling.value
  ],
  () => {
    insuranceForm.value.fname = contactForm.value.firstName;
    insuranceForm.value.lname = contactForm.value.lastName;
    insuranceForm.value.email = contactForm.value.email;
    insuranceForm.value.contact = contactForm.value.phone;
    syncAddressToInsurance();
    scheduleShippingQuote();
  }
);

watch(
  () => sameShippingAsBilling.value,
  async (same) => {
    if (same) return;
    if (!shippingForm.value.countryId) {
      shippingForm.value.countryId = addressForm.value.countryId || PH_COUNTRY_ID;
    }
    if (!shippingZones.value.length) {
      await loadShippingZones(shippingForm.value.countryId, { keepZone: true });
      applyPendingZone(shippingForm.value, shippingZones.value, 'shipping');
    }
  }
);

watch(
  () => quantity.value,
  () => {
    scheduleShippingQuote();
  }
);

watch(
  () => route.query.qty,
  (nextQty) => {
    const clamped = clampQuantity(nextQty);
    if (quantity.value !== clamped) {
      quantity.value = clamped;
      clearCheckoutReview();
    }
  }
);

function proceedToPayDollar() {
  if (!pendingGateway.value) {
    errorMessage.value = 'Payment session expired. Please click Pay again.';
    clearCheckoutReview();
    return;
  }

  proceeding.value = true;
  errorMessage.value = '';
  const result = performPayDollarRedirect(pendingGateway.value);
  if (!result.ok) {
    errorMessage.value = result.error;
    proceeding.value = false;
  }
}

async function loadProduct() {
  if (!locationId.value || !sku.value) {
    errorMessage.value = 'Missing locationId or sku in checkout link';
    loading.value = false;
    return;
  }

  try {
    const { data } = await fetchGhlPaymentCatalogProduct(locationId.value, sku.value);
    if (!data || !data.success || !data.product) {
      errorMessage.value = (data && data.message) || 'Product not found';
      return;
    }
    product.value = data.product;
  } catch (err) {
    const errResponse = err && err.response;
    const errData = errResponse && errResponse.data;
    errorMessage.value = (errData && errData.message) || err.message || 'Failed to load product';
  } finally {
    loading.value = false;
  }
}

async function submitPayment() {
  if (!product.value) {
    errorMessage.value = 'Product not loaded';
    return;
  }

  if (!contactForm.value.firstName.trim() || !contactForm.value.lastName.trim()) {
    errorMessage.value = 'First name and last name are required';
    return;
  }

  if (!contactForm.value.email.trim()) {
    errorMessage.value = 'Email is required';
    return;
  }

  if (addressIncomplete(addressForm.value)) {
    errorMessage.value = 'Street address, city, state, and postal code are required';
    return;
  }

  if (!sameShippingAsBilling.value && addressIncomplete(shippingForm.value)) {
    errorMessage.value = 'Shipping street address, city, state, and postal code are required';
    return;
  }

  if (!shippingSelectionReady(shippingQuoteState)) {
    if (requiresShipping.value && !shippingMethods.value.length) {
      errorMessage.value = shippingQuoteError.value || 'No delivery options are available for this address';
    } else {
      errorMessage.value = 'Please select a delivery method';
    }
    return;
  }

  submitting.value = true;
  errorMessage.value = '';

  try {
    const billingAddress = buildAddressPayload(addressForm.value, billingZones.value);
    const payload = {
      locationId: locationId.value,
      sku: sku.value,
      qty: quantity.value,
      contact: {
        name: fullName.value,
        email: contactForm.value.email.trim(),
        phone: contactForm.value.phone.trim(),
        billingAddress,
        shippingAddress: sameShippingAsBilling.value
          ? billingAddress
          : buildAddressPayload(shippingForm.value, shippingZones.value),
        sameShippingAsBilling: sameShippingAsBilling.value
      }
    };

    if (ecpid.value) {
      payload.ecpid = ecpid.value;
    }

    if (prefillToken.value) {
      payload.pf = prefillToken.value;
      saveCachedPrefill(prefillToken.value, currentPrefillSnapshot());
    }

    if (hasInsuranceCoverage.value) {
      syncAddressToInsurance();
      payload.insuranceData = { ...insuranceForm.value };
    }

    if (selectedShippingMethod.value) {
      payload.shippingMethod = selectedShippingMethod.value;
    }

    const { data } = await submitGhlCheckoutLink(payload);
    if (!data || !data.success) {
      errorMessage.value = (data && (data.message || data.error)) || 'Payment could not be started';
      return;
    }

    if (!data.gateway) {
      errorMessage.value = 'Payment gateway response was incomplete';
      return;
    }

    pendingGateway.value = data.gateway;
    orderSummary.value = data.orderSummary || null;
    checkoutPhase.value = 'review';
  } catch (err) {
    const errResponse = err && err.response;
    const errData = errResponse && errResponse.data;
    errorMessage.value = (errData && (errData.message || errData.error)) || err.message || 'Payment request failed';
  } finally {
    submitting.value = false;
  }
}

onMounted(async () => {
  paymentStatus.value = String(route.query.status || '');
  countries.value = await fetchCountries();
  applyQueryPrefills();
  await hydrateAddressSelects();
  if (!isTerminalStatus.value) {
    await loadProduct();
    scheduleShippingQuote();
  } else {
    loading.value = false;
  }
});
</script>

<template>
  <div class="checkout-page">
    <div v-if="isTerminalStatus" class="checkout-card checkout-card--status">
      <div class="status-panel">
        <h2 v-if="paymentStatus === 'success'">Payment submitted</h2>
        <h2 v-else-if="paymentStatus === 'fail'">Payment failed</h2>
        <h2 v-else>Payment cancelled</h2>
        <p>You may close this window.</p>
      </div>
    </div>

    <div v-else-if="loading" class="checkout-card checkout-card--status">
      <div class="status-panel">
        <p>Loading checkout...</p>
      </div>
    </div>

    <div v-else-if="errorMessage && !product" class="checkout-card checkout-card--status">
      <div class="status-panel">
        <h2>Checkout unavailable</h2>
        <p class="error">{{ errorMessage }}</p>
      </div>
    </div>

    <div v-else class="checkout-card">
      <aside class="checkout-summary">
        <div class="brand">
          <img
            src="media/ai247-logo.png"
            alt="AI 24/7 SaaS"
            class="brand-logo"
          />
          <span class="brand-name">AI 24/7 SaaS</span>
        </div>

        <section v-if="product" class="summary-section">
          <h2 class="section-title">One Time Products</h2>

          <div class="product-line">
            <div class="product-line-main">
              <div class="product-thumb">
                <img
                  v-if="product.imageUrl"
                  :src="product.imageUrl"
                  :alt="product.name"
                  class="product-thumb-image"
                />
                <span v-else class="product-thumb-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3L3 8v8l9 5 9-5V8l-9-5z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M3 8l9 5 9-5M12 13v8"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
              <div class="product-copy">
                <p class="product-name">{{ product.name }}</p>
                <p v-if="formattedUnitLineDetail" class="product-subtitle">{{ formattedUnitLineDetail }}</p>
                <p v-else class="product-subtitle">{{ product.name }}</p>
                <div class="qty-control" role="group" aria-label="Quantity">
                  <button
                    type="button"
                    class="qty-control-btn"
                    :disabled="isReviewPhase || quantity <= MIN_QTY"
                    aria-label="Decrease quantity"
                    @click="decrementQuantity"
                  >
                    −
                  </button>
                  <input
                    class="qty-control-input"
                    type="number"
                    :min="MIN_QTY"
                    :max="MAX_QTY"
                    inputmode="numeric"
                    aria-label="Quantity"
                    :value="quantity"
                    :disabled="isReviewPhase"
                    @input="onQuantityInput"
                    @blur="onQuantityBlur"
                  />
                  <button
                    type="button"
                    class="qty-control-btn"
                    :disabled="isReviewPhase || quantity >= MAX_QTY"
                    aria-label="Increase quantity"
                    @click="incrementQuantity"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <p class="product-price">{{ formattedLineAmount }}</p>
          </div>

          <div class="summary-divider" />

          <template v-if="isReviewPhase && orderSummary">
            <div
              v-for="line in orderSummary.lines"
              :key="line.code"
              class="summary-row"
            >
              <span>{{ line.title }}</span>
              <span>{{ formatCurrency(line.amount) }}</span>
            </div>

            <div class="summary-divider" />

            <div class="summary-row summary-row--total">
              <span>Total</span>
              <span>{{ formattedReviewTotal }}</span>
            </div>
          </template>

          <template v-else>
            <div class="summary-row">
              <span>Product subtotal (estimate)</span>
              <span>{{ formattedLineAmount }}</span>
            </div>
            <div v-if="requiresShipping && selectedShippingMethod" class="summary-row">
              <span>Delivery (estimate)</span>
              <span>{{ formatCurrency(estimatedShippingCost) }}</span>
            </div>
            <div v-if="requiresShipping && selectedShippingMethod" class="summary-row summary-row--total">
              <span>Estimated total</span>
              <span>{{ formattedEstimatedTotal }}</span>
            </div>

            <p class="summary-note">
              Convenience fees and other charges may apply; final total confirmed before payment.
            </p>
          </template>
        </section>

        <footer class="summary-footer">Powered by AI 24/7 SaaS</footer>
      </aside>

      <main class="checkout-form">
        <section v-if="isReviewPhase" class="review-panel">
          <h2 class="review-title">Confirm payment</h2>
          <p class="review-intro">
            Review your order total before proceeding to PayDollar.
          </p>

          <div v-if="orderSummary" class="review-breakdown">
            <div
              v-for="line in orderSummary.lines"
              :key="`review-${line.code}`"
              class="review-row"
            >
              <span>{{ line.title }}</span>
              <span>{{ formatCurrency(line.amount) }}</span>
            </div>
            <div class="review-row review-row--total">
              <span>Total</span>
              <span>{{ formattedReviewTotal }}</span>
            </div>
          </div>

          <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

          <div class="review-actions">
            <button
              class="pay-button"
              type="button"
              :disabled="proceeding || !pendingGateway"
              @click="proceedToPayDollar"
            >
              {{ proceeding ? 'Redirecting...' : 'Proceed to PayDollar' }}
            </button>
            <button
              class="back-button"
              type="button"
              :disabled="proceeding"
              @click="backToDetails"
            >
              Back
            </button>
          </div>
        </section>

        <template v-else>
        <div class="form-row form-row--split">
          <label class="field">
            <span class="field-label">First Name <span class="required">*</span></span>
            <input
              v-model="contactForm.firstName"
              type="text"
              placeholder="First Name"
              autocomplete="given-name"
              required
            />
          </label>
          <label class="field">
            <span class="field-label">Last Name <span class="required">*</span></span>
            <input
              v-model="contactForm.lastName"
              type="text"
              placeholder="Last Name"
              autocomplete="family-name"
              required
            />
          </label>
        </div>

        <label class="field">
          <span class="field-label">Email <span class="required">*</span></span>
          <input
            v-model="contactForm.email"
            type="email"
            placeholder="abc@gmail.com"
            autocomplete="email"
            required
          />
        </label>

        <label class="field">
          <span class="field-label">Phone</span>
          <input
            v-model="contactForm.phone"
            type="tel"
            placeholder="Phone"
            autocomplete="tel"
          />
        </label>

        <section class="address-section">
          <h3 class="address-title">Billing address</h3>
          <label class="field">
            <span class="field-label">Street address <span class="required">*</span></span>
            <input
              v-model="addressForm.address1"
              type="text"
              placeholder="Street address"
              autocomplete="street-address"
              required
            />
          </label>
          <div class="form-row form-row--split">
            <label class="field">
              <span class="field-label">City <span class="required">*</span></span>
              <input
                v-model="addressForm.city"
                type="text"
                placeholder="City"
                autocomplete="address-level2"
                required
              />
            </label>
            <label class="field">
              <span class="field-label">Postal code <span class="required">*</span></span>
              <input
                v-model="addressForm.postcode"
                type="text"
                placeholder="Postal code"
                autocomplete="postal-code"
                required
              />
            </label>
          </div>
          <div class="form-row form-row--split">
            <label class="field">
              <span class="field-label">Country <span class="required">*</span></span>
              <select
                v-model.number="addressForm.countryId"
                autocomplete="country"
                required
                @change="onBillingCountryChange"
              >
                <option
                  v-for="country in countries"
                  :key="country.country_id"
                  :value="Number(country.country_id)"
                >
                  {{ country.name }}
                </option>
              </select>
            </label>
            <label class="field">
              <span class="field-label">State / Province <span class="required">*</span></span>
              <select
                v-model.number="addressForm.zoneId"
                autocomplete="address-level1"
                required
              >
                <option :value="0">Select region / state</option>
                <option
                  v-for="zone in billingZones"
                  :key="zone.zone_id"
                  :value="Number(zone.zone_id)"
                >
                  {{ zone.name }}
                </option>
              </select>
            </label>
          </div>

          <label class="same-shipping">
            <input v-model="sameShippingAsBilling" type="checkbox" />
            <span>Shipping address is the same as billing</span>
          </label>

          <template v-if="!sameShippingAsBilling">
            <h3 class="address-title">Shipping address</h3>
            <label class="field">
              <span class="field-label">Street address <span class="required">*</span></span>
              <input
                v-model="shippingForm.address1"
                type="text"
                placeholder="Street address"
                autocomplete="shipping street-address"
                required
              />
            </label>
            <div class="form-row form-row--split">
              <label class="field">
                <span class="field-label">City <span class="required">*</span></span>
                <input
                  v-model="shippingForm.city"
                  type="text"
                  placeholder="City"
                  autocomplete="shipping address-level2"
                  required
                />
              </label>
              <label class="field">
                <span class="field-label">Postal code <span class="required">*</span></span>
                <input
                  v-model="shippingForm.postcode"
                  type="text"
                  placeholder="Postal code"
                  autocomplete="shipping postal-code"
                  required
                />
              </label>
            </div>
            <div class="form-row form-row--split">
              <label class="field">
                <span class="field-label">Country <span class="required">*</span></span>
                <select
                  v-model.number="shippingForm.countryId"
                  autocomplete="shipping country"
                  required
                  @change="onShippingCountryChange"
                >
                  <option
                    v-for="country in countries"
                    :key="`ship-${country.country_id}`"
                    :value="Number(country.country_id)"
                  >
                    {{ country.name }}
                  </option>
                </select>
              </label>
              <label class="field">
                <span class="field-label">State / Province <span class="required">*</span></span>
                <select
                  v-model.number="shippingForm.zoneId"
                  autocomplete="shipping address-level1"
                  required
                >
                  <option :value="0">Select region / state</option>
                  <option
                    v-for="zone in shippingZones"
                    :key="`ship-${zone.zone_id}`"
                    :value="Number(zone.zone_id)"
                  >
                    {{ zone.name }}
                  </option>
                </select>
              </label>
            </div>
          </template>
        </section>

        <section v-if="requiresShipping || quotingShipping || shippingQuoteError" class="shipping-method-section">
          <h3 class="address-title">Delivery method</h3>
          <p v-if="quotingShipping" class="shipping-note">Loading delivery options...</p>
          <p v-else-if="shippingQuoteError" class="error">{{ shippingQuoteError }}</p>
          <p v-else-if="requiresShipping && !shippingMethods.length" class="error">
            No delivery options are available for this address.
          </p>
          <div v-else class="shipping-methods">
            <label
              v-for="method in shippingMethods"
              :key="method.code"
              class="shipping-method-option"
            >
              <input
                v-model="selectedShippingMethod"
                type="radio"
                name="shippingMethod"
                :value="method.code"
              />
              <span>{{ method.title }} — {{ method.text || formatCurrency(method.cost) }}</span>
            </label>
          </div>
        </section>

        <section v-if="hasInsuranceCoverage" class="insurance-section">
          <h3 class="insurance-title">Bronze Coverage Details</h3>
          <div class="form-row form-row--split">
            <label class="field">
              <span class="field-label">Middle name</span>
              <input v-model="insuranceForm.mname" type="text" />
            </label>
            <label class="field">
              <span class="field-label">Date of birth <span class="required">*</span></span>
              <input v-model="insuranceForm.dob" type="date" required />
            </label>
          </div>
          <label class="field">
            <span class="field-label">Nationality</span>
            <input v-model="insuranceForm.nationality" type="text" />
          </label>

          <h4 class="insurance-subtitle">Beneficiary</h4>
          <div class="form-row form-row--split">
            <label class="field">
              <span class="field-label">First name <span class="required">*</span></span>
              <input v-model="insuranceForm.benefFname" type="text" required />
            </label>
            <label class="field">
              <span class="field-label">Last name <span class="required">*</span></span>
              <input v-model="insuranceForm.benefLname" type="text" required />
            </label>
          </div>
          <div class="form-row form-row--split">
            <label class="field">
              <span class="field-label">Date of birth <span class="required">*</span></span>
              <input v-model="insuranceForm.benefDob" type="date" required />
            </label>
            <label class="field">
              <span class="field-label">Contact <span class="required">*</span></span>
              <input v-model="insuranceForm.benefContact" type="tel" required />
            </label>
          </div>
          <label class="field">
            <span class="field-label">Relationship</span>
            <input v-model="insuranceForm.benefRelationship" type="text" />
          </label>
        </section>

        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

        <div class="form-divider" />

        <button
          class="pay-button"
          type="button"
          :disabled="submitting || !product || !canProceedToReview"
          @click="submitPayment"
        >
          {{ submitting ? 'Processing...' : 'Next' }}
        </button>
        </template>
      </main>
    </div>
  </div>
</template>
